import type { AppDispatch, RootState } from '@/store';
import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import { getChargepointsUptimeSummary } from 'services/chargePoints';
import type { ChargePointUptimeSummary } from 'types/swagger';

import { showSnackbarMessage } from './Common';

// These keys must coincide with endpoint parameters to camel case
export interface UptimeFiltersType {
  monthStart: number;
  yearStart: number;
  monthEnd: number;
  yearEnd: number;
  asset?: number;
  chargePointGroup?: number;
  chargePointIds?: number[];
}

enum UptimeActionTypes {
  SET_CHARGEPOINT_UPTIME_SUMMARY_BY_ID = 'SET_CHARGEPOINT_UPTIME_SUMMARY_BY_ID',
  SET_IS_FETCHING_CHARGEPOINT_UPTIME_SUMMARY_BY_ID = 'SET_IS_FETCHING_CHARGEPOINT_UPTIME_SUMMARY_BY_ID',
}

export const setUptimeSummary = (
  chargepointsUptimeSummary: ChargePointUptimeSummary[]
): PayloadAction<ChargePointUptimeSummary[]> => ({
  type: UptimeActionTypes.SET_CHARGEPOINT_UPTIME_SUMMARY_BY_ID,
  payload: chargepointsUptimeSummary,
});

export const setIsFetchingUptimeSummary = (isFetching: boolean): PayloadAction<boolean> => ({
  type: UptimeActionTypes.SET_IS_FETCHING_CHARGEPOINT_UPTIME_SUMMARY_BY_ID,
  payload: isFetching,
});

type ChargepointsUptimeSummaryState = {
  chargepointsUptimeSummary: {
    all: ChargePointUptimeSummary[];
    byId: Record<string, ChargePointUptimeSummary>;
  };
  isFetchingChargepointsUptimeSummary: boolean;
};

const INIT_STATE: ChargepointsUptimeSummaryState = {
  chargepointsUptimeSummary: {
    all: [],
    byId: {},
  },
  isFetchingChargepointsUptimeSummary: false,
};

export default function uptimeReducer(state = INIT_STATE, action: UnknownAction) {
  const { type, payload } = action;

  switch (type) {
    case UptimeActionTypes.SET_CHARGEPOINT_UPTIME_SUMMARY_BY_ID: {
      return {
        ...state,
        chargepointsUptimeSummary: {
          all: payload as ChargePointUptimeSummary[],
          byId: Object.fromEntries(
            (payload as ChargePointUptimeSummary[]).map((chargepointUptimeSummary) => [
              chargepointUptimeSummary.charge_point_id,
              chargepointUptimeSummary,
            ])
          ),
        },
      };
    }
    case UptimeActionTypes.SET_IS_FETCHING_CHARGEPOINT_UPTIME_SUMMARY_BY_ID: {
      return {
        ...state,
        isFetchingChargepointsUptimeSummary: payload as boolean,
      };
    }
    default:
      return state;
  }
}

export const fetchChargepointsUptimeSummary =
  (filters: UptimeFiltersType) => async (dispatch: AppDispatch) => {
    dispatch(setIsFetchingUptimeSummary(true));

    getChargepointsUptimeSummary(filters)
      .then((res) => {
        if (res.data.results) {
          dispatch(setUptimeSummary(res.data.results));
          return res;
        }
        return res;
      })
      .catch((err) => {
        dispatch(showSnackbarMessage('Hubo un error al obtener la información.'));
        return err;
      })
      .finally(() => dispatch(setIsFetchingUptimeSummary(false)));
  };

export const selectUptimeSummaryById = (state: RootState) =>
  state.uptime.chargepointsUptimeSummary.byId;
export const selectIsFetchingUptimeSummary = (state: RootState) =>
  state.uptime.isFetchingChargepointsUptimeSummary;
